import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, LogIn, Gift, ArrowRight, MapPin, Lock, HeartHandshake, Loader2, Ticket } from "lucide-react";
import api, { fileUrl, apiErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";

export default function Neighbors() {
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [groups, setGroups] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/neighbors/eligibility").then((r) => setElig(r.data)).catch(() => setElig({ eligible: false, supported_count: 0, threshold: 5 })),
      api.get("/neighbors/groups").then((r) => setGroups(r.data)).catch(() => setGroups([])),
      api.get("/neighbors/giftcards/mine").then((r) => setCards(r.data)).catch(() => setCards([])),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center text-copper font-serif"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;

  const pct = elig ? Math.min(100, Math.round((elig.supported_count / elig.threshold) * 100)) : 0;

  return (
    <div data-testid="neighbors-page" className="max-w-3xl mx-auto px-3.5 sm:px-6 py-6 sm:py-8">
      <PageHeader icon={HeartHandshake} eyebrow="Neighbors in Need" title="Your local circles of care"
        subtitle="Private groups for people who live close together — pool a little money and gift cards, then lift up a neighbor, throw a holiday party, or surprise every kid on the block. Nobody left out, nobody singled out. 💛" />

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2.5 sm:gap-3 mb-6 max-w-md mx-auto">
        <Button data-testid="open-create-group" onClick={() => { if (!elig?.eligible) { toast.error(`Support ${elig?.threshold}+ people first to unlock creating groups.`); return; } setCreateOpen(true); }}
          className="rounded-full bg-copper hover:bg-copper/90 text-white min-h-[48px]">
          <Plus className="w-4 h-4 mr-1.5" /> Create a group
        </Button>
        <Button data-testid="open-join-group" onClick={() => setJoinOpen(true)} variant="outline" className="rounded-full min-h-[48px] border-copper/40 text-copper hover:bg-copper/10">
          <LogIn className="w-4 h-4 mr-1.5" /> Join with code
        </Button>
      </div>

      {/* Eligibility gate */}
      {elig && !elig.eligible && (
        <div data-testid="eligibility-card" className="bg-penny/10 border border-penny/25 rounded-2xl p-4 sm:p-5 mb-6">
          <div className="flex items-center gap-2 text-bronze font-medium"><Lock className="w-4 h-4 text-penny" /> Unlock creating groups</div>
          <p className="text-sm text-foreground/70 mt-1">To keep this feature safe, you can start a group once you've personally supported <b>{elig.threshold} different people</b>. You can always join a neighbor's group with a code in the meantime.</p>
          <div className="h-3 bg-secondary rounded-full mt-3 overflow-hidden"><div className="h-full bg-penny rounded-full transition-all" style={{ width: `${pct}%` }} data-testid="eligibility-bar" /></div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs text-muted-foreground">{elig.supported_count} / {elig.threshold} people supported</span>
            <button onClick={() => navigate("/discover")} className="text-xs font-medium text-copper hover:underline">Find people to support →</button>
          </div>
        </div>
      )}

      {/* My groups */}
      <h2 className="font-serif text-xl font-semibold text-bronze mb-3 text-center">My groups</h2>
      {groups.length === 0 ? (
        <div className="flex flex-col items-center text-center py-10 px-4 bg-card border border-border rounded-2xl mb-8">
          <Users className="w-9 h-9 text-copper/60" />
          <p className="text-muted-foreground mt-2 text-sm">You're not in any groups yet. Create one or join with a neighbor's code.</p>
        </div>
      ) : (
        <div className="space-y-3 mb-8" data-testid="my-groups-list">
          {groups.map((g) => (
            <button key={g.group_id} data-testid={`group-row-${g.group_id}`} onClick={() => navigate(`/neighbors/${g.group_id}`)}
              className="w-full text-left bg-card border border-border rounded-2xl p-4 hover:border-copper/40 transition-all flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-copper/15 text-copper flex items-center justify-center shrink-0"><Users className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-bronze truncate">{g.name}</p>
                <p className="text-xs text-muted-foreground truncate flex items-center gap-2">
                  {g.area && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {g.area}</span>}
                  <span>· {g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono font-bold text-copper">${(g.fund_balance || 0).toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide">in the pot</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* Gift cards for me */}
      <div className="flex flex-col items-center gap-2 mb-3">
        <h2 className="font-serif text-xl font-semibold text-bronze text-center">My gift cards</h2>
        <Button data-testid="open-redeem" onClick={() => setRedeemOpen(true)} variant="outline" size="sm" className="rounded-full">
          <Ticket className="w-4 h-4 mr-1.5" /> Redeem a code
        </Button>
      </div>
      {cards.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-card border border-border rounded-2xl p-4">No gift cards waiting for you. If a neighbor sends you one, it'll show up here to redeem into your wallet.</p>
      ) : (
        <div className="space-y-2.5" data-testid="my-giftcards-list">
          {cards.map((c) => (
            <div key={c.card_id} className="bg-gradient-to-br from-sage/15 to-penny/10 border border-sage/30 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-bronze flex items-center gap-1.5"><Gift className="w-4 h-4 text-sage" /> {c.label}</p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">{c.code}</p>
                {c.note && <p className="text-xs text-foreground/60 mt-0.5 break-words">{c.note}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="font-mono font-bold text-sage text-lg">${(c.balance || 0).toFixed(2)}</p>
                <Button size="sm" data-testid={`redeem-card-${c.card_id}`} onClick={() => redeemCode(c.code, load)} className="rounded-full bg-sage hover:bg-sage/90 text-white mt-1 h-8">Redeem</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateGroupDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(g) => { setCreateOpen(false); navigate(`/neighbors/${g.group_id}`); }} />
      <JoinDialog open={joinOpen} onClose={() => setJoinOpen(false)} onJoined={(gid) => { setJoinOpen(false); navigate(`/neighbors/${gid}`); }} />
      <RedeemDialog open={redeemOpen} onClose={() => setRedeemOpen(false)} onDone={() => { setRedeemOpen(false); load(); }} />
    </div>
  );
}

async function redeemCode(code, reload) {
  try { const { data } = await api.post("/neighbors/giftcards/redeem", { code }); toast.success(`Redeemed $${data.amount.toFixed(2)} into your wallet 💛`); reload(); }
  catch (e) { toast.error(apiErr(e)); }
}

function CreateGroupDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState(""); const [area, setArea] = useState(""); const [desc, setDesc] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(""); setArea(""); setDesc(""); } }, [open]);
  const create = async () => {
    if (!name.trim()) { toast.error("Give your group a name."); return; }
    setBusy(true);
    try { const { data } = await api.post("/neighbors/groups", { name, area, description: desc }); toast.success("Group created 💛 Share the code with your neighbors!"); onCreated(data); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-group-dialog" className="sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze">Start a Neighbors group</DialogTitle><DialogDescription className="sr-only">Create a private local group.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Group name</Label><Input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Maple Street Neighbors" className="rounded-xl mt-1" /></div>
          <div><Label>Area <span className="text-muted-foreground font-normal">(neighborhood, street or zip)</span></Label><Input data-testid="group-area-input" value={area} onChange={(e) => setArea(e.target.value)} placeholder="Maple St · 90210" className="rounded-xl mt-1" /></div>
          <div><Label>What's this group about?</Label><Textarea data-testid="group-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="A private circle for our block to help each other and throw little events for the kids." className="rounded-xl mt-1" /></div>
        </div>
        <DialogFooter className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="create-group-submit" onClick={create} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create group"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JoinDialog({ open, onClose, onJoined }) {
  const [code, setCode] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setCode(""); }, [open]);
  const join = async () => {
    if (!code.trim()) { toast.error("Enter the join code."); return; }
    setBusy(true);
    try { const { data } = await api.post("/neighbors/join", { code }); toast.success(data.already ? "You're already in this group." : "Joined 💛"); onJoined(data.group_id); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="join-group-dialog" className="sm:max-w-sm rounded-3xl">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze">Join a group</DialogTitle><DialogDescription className="sr-only">Enter a private group's join code.</DialogDescription></DialogHeader>
        <Label>Join code</Label>
        <Input data-testid="join-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. 1XGLSP" className="rounded-xl mt-1 font-mono tracking-widest text-center text-lg" />
        <DialogFooter className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="join-group-submit" onClick={join} disabled={busy} className="rounded-full bg-copper hover:bg-copper/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RedeemDialog({ open, onClose, onDone }) {
  const [code, setCode] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setCode(""); }, [open]);
  const redeem = async () => {
    if (!code.trim()) { toast.error("Enter the gift card code."); return; }
    setBusy(true);
    try { const { data } = await api.post("/neighbors/giftcards/redeem", { code }); toast.success(`Redeemed $${data.amount.toFixed(2)} into your wallet 💛`); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="redeem-dialog" className="sm:max-w-sm rounded-3xl">
        <DialogHeader><DialogTitle className="font-serif text-xl text-bronze">Redeem a gift card</DialogTitle><DialogDescription className="sr-only">Redeem a gift card code into your wallet.</DialogDescription></DialogHeader>
        <Label>Gift card code</Label>
        <Input data-testid="redeem-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GC-XXXXXXXX" className="rounded-xl mt-1 font-mono tracking-widest text-center" />
        <p className="text-xs text-muted-foreground mt-1">The balance goes straight into your Haplap wallet, ready to cash out.</p>
        <DialogFooter className="grid grid-cols-2 gap-2.5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button data-testid="redeem-submit" onClick={redeem} disabled={busy} className="rounded-full bg-sage hover:bg-sage/90 text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Redeem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
